import { action, computed, observable } from "mobx";
import throttle from "lodash.throttle";

import { characterColors } from "../components/theme";

const SAVE_THROTTLE_TIME = 30 * 1000;

class Link {
  @computed
  get node() {
    return this.store.movie.nodes[this.destination];
  }

  constructor(store, data) {
    this.store = store;
    Object.assign(this, data);
  }
}

export default class Node {
  store;
  @observable lastSyncTime = Date.now();
  @observable isSynced = true;
  @observable areDiscoveriesSynced = true;

  @computed
  get asset() {
    return this.store.movie.assets[this.assetId];
  }

  @computed
  get smallImageUrl() {
    return `${this.store.config.assetPath}characters/407px/407_${
      this.asset.hash
    }@1x.jpg`;
  }

  @computed
  get mediumImageUrl() {
    return `${this.store.config.assetPath}characters/1024px/1024_${
      this.asset.hash
    }.jpg`;
  }

  @computed
  get largeImageUrl() {
    return `${this.store.config.assetPath}characters/1920px/1920_${
      this.asset.hash
    }@1x.jpg`;
  }

  @computed
  get stillImageUrl() {
    const id = this.asset.url || `node${this.asset.title.match(/\d{2}/)[0]}`;
    return `${this.store.config.assetPath}nodes/720p/${id}.jpg`;
  }

  @computed
  get videoPath() {
    return this.store.makeVideoPath(
      this.asset.url || `node${this.asset.title.match(/\d{2}/)[0]}`
    );
  }

  @computed
  get session() {
    return this.store.session.nodes[this.uuid];
  }

  @computed
  get discoveries() {
    const { movie } = this.store;
    return movie.nodeToDiscoveries[this.uuid] || [];
  }

  @computed
  get unlockedDiscoveries() {
    return this.discoveries.filter(discovery => {
      return discovery.isUnlocked;
    });
  }

  @computed
  get color() {
    return characterColors[this.characterName];
  }

  @computed
  get parents() {
    return this.store.movie.nodeToParents[this.uuid] || [];
  }

  @computed
  get rawProgress() {
    return this.session ? this.session.position / this.asset.duration : 0;
  }

  @computed
  get progress() {
    if ((this.session && this.session.complete) || this.rawProgress >= 0.95) {
      return 1;
    } else if (this.rawProgress > 0 && this.rawProgress < 0.02) {
      return 0.02;
    } else {
      return parseFloat(this.rawProgress.toFixed(2));
    }
  }

  @computed
  get isComplete() {
    return this.progress >= 0.95;
  }

  @computed
  get isLocked() {
    return (
      this.parents.length > 0 &&
      this.parents.every(parent => !parent.isComplete)
    );
  }

  @computed
  get isWatching() {
    const { session, movie } = this.store;

    if (session.history.length > 0) {
      const lastViewedId = session.history[session.historyIndex];

      return (
        (!this.isLocked && this.uuid === lastViewedId) ||
        (movie.completedChapters === 0 && this === movie.startNode)
      );
    } else {
      return this === movie.startNode;
    }
  }

  @computed
  get timeToShowLinks() {
    return this.asset.duration - this.transitionStartTime;
  }

  constructor(store, { asset, links, ...data }) {
    this.store = store;

    this.assetId = asset;
    this.links = links.map(link => new Link(store, link));
    Object.assign(this, data);
  }

  @action
  updatePosition = (position, forceSave = false) => {
    const { duration } = this.asset;
    const progress = position / duration;

    const wasComplete = this.session.complete;
    this.session.position = position;
    this.session.complete = this.session.complete || progress >= 0.95;

    this.discoveries.filter(x => x.time <= position).forEach(x => {
      if (!(x.session.unlocked && x.session.notified)) {
        this.areDiscoveriesSynced = false;

        x.session.unlocked = true;
        x.session.notified = true;
      }
    });

    this.isSynced = false;

    if ((this.isComplete && !wasComplete) || forceSave) {
      this.saveSession();
    } else {
      this.saveSessionThrottled();
    }
  };

  @action
  unlockDiscoveriesUpTo = position => {
    if (this.areDiscoveriesSynced) {
      return;
    }

    const { config, profile } = this.store;
    const url = `${config.routes.session}/unlock/${config.movieID}/${
      profile._id
    }`;

    const discoveries = this.discoveries
      .filter(x => x.time <= position)
      .map(x => {
        return {
          id: x._id,
          unlocked: true,
          notified: true,
        };
      });

    return this.store.axios.post(url, { discoveries }).then(() => {
      this.areDiscoveriesSynced = true;
      return Promise.resolve();
    });
  };

  @action
  saveSession = () => {
    const { config, profile } = this.store;
    const url = `${config.routes.session}/${config.movieID}/${profile._id}`;

    if (this.isSynced) {
      return Promise.resolve();
    }

    this.unlockDiscoveriesUpTo(this.session.position);

    return this.store.axios
      .post(url, {
        node: this.uuid,
        position: this.session.position,
        complete: this.session.complete,
      })
      .then(({ data }) => {
        if (data.err === 0) {
          this.isSynced = true;
          return this.store.fetchHistory();
        } else {
          return Promise.reject(data.err);
        }
      });
  };

  @action saveSessionThrottled = throttle(this.saveSession, SAVE_THROTTLE_TIME);
}



// WEBPACK FOOTER //
// ./src/models/Node.js